<template>
    <v-row
        no-gutters
        class="pl-9 pr-8 pt-8"
    >
        <v-col>
            <div class="title mb-3">{{ $t("Send emails") }}</div>
            <div class="body-2">{{ $t("Uku securely integrates with your email to help you manage and automate communication") }}</div>
            <div class="ml-5">
                <v-radio-group
                    :disabled="!canEditEmailIntegrationsEnabled"
                    :value="emailIntegrationsEnabled"
                    hide-details
                    @change="setEmailIntegrationsEnabled"
                >
                    <v-radio
                        :label="$t('Use {0} address').format(info_email)"
                        :value="false"
                    ></v-radio>
                    <v-radio
                        :label="$t('connect your email address')"
                        :value="true"
                    ></v-radio>
                </v-radio-group>
                <div class="ml-10 mt-6">
                    <v-row
                        v-if="canEditEmailIntegrationsEnabled || emailIntegrationsEnabled"
                        no-gutters
                    >
                        <v-col>
                            <IntegrationButton
                                v-for="(button, index) in EmailIntegrationModule.getIntegrations(companyId)"
                                :key="index"
                                :name="button.name"
                                :img="button.img"
                                @click="integrationSelected(button.link)"
                            />
                        </v-col>
                    </v-row>
                    <div
                        v-if="canEditEmailIntegrationsEnabled || emailIntegrationsEnabled"
                        class="body-2 primary--text cursor-pointer mt-5 mb-3 cursor-pointer"
                        @click="connectSmtp"
                    >
                        {{ $t('Connect with Microsoft Exchange or other email server(SMTP)') }}
                    </div>
                    <v-row
                        v-if="emailIntegrationsEnabled"
                        no-gutters
                    >
                        <v-col>
                            <data-table
                                :items="emailAutomationEmails"
                                :headers="EmailIntegrationModule.getEmailIntegrationTableHeaders()"
                                row-clickable
                                @click:row="setEditEmailIntegration($event.id)"
                            >
                                <template v-slot:item.email="{ item }">
                                    <v-chip
                                        pill
                                        color="g2"
                                        class="px-4"
                                        :class="item.id === defaultEmailIntegrationId ? 'pr-3' : ''"
                                    >
                                        {{ item.email }}
                                        <v-avatar
                                            v-if="item.id === defaultEmailIntegrationId"
                                            right
                                        >
                                            <v-btn
                                                icon
                                                right
                                                small
                                                color="secondary"
                                            >
                                                <v-icon size="18">star</v-icon>
                                            </v-btn>
                                        </v-avatar>
                                    </v-chip>
                                </template>
                                <template v-slot:item.visibility="{ item }">
                                    <div>{{ EmailIntegrationModule.getTableEmailIntegrationVisibilityName(getVisibleMembersByCompany(companyId), item.visibility, item.owner_id) }}</div>
                                </template>
                            </data-table>
                        </v-col>
                    </v-row>
                </div>
            </div>
        </v-col>
        <EditEmailIntegration
            v-if="selectedEmail"
            :email="selectedEmail"
            :errors="errors"
            :company-id="companyId"
            @clear-error="clearErrors"
            @close="onClose"
            @delete="onDelete"
            @save="onSave"
            @send-test-email="sendTestEmail($event)"
            @reconnect="onReconnect($event)"
        />
    </v-row>
</template>

<script>
import {computed, onMounted, onUnmounted, reactive, ref, watch} from "@vue/composition-api";
import appStoreDialog from "@/store/modules/AppStoreDialog/appStoreDialog";
import {get} from "@/helpers/get-set";
import {$t} from "@/i18n";
import Vue from 'vue';
import IntegrationButton from "@/components/Buttons/IntegrationButton";
import ApiEmailIntegration from "@/api/email_automation/email_integration"
import EditEmailIntegration from "@/components/Dialog/EmailIntegration/Index"
import errorDialog from "@/views/ErrorDialog/errorDialog";
import WS from "@/utility/webSocket";
import RightsModule from "@/composables/use-rights";
import ApiAppStore from "@/api/app_store/appStore"
import AccountModule from "@/store/modules/account";
import EmailIntegrationModule from "@/modules/emailIntegration/emailIntegration"
import DialogModule from "@/store/modules/dialog";
import AppModule from "@/store/modules/app"
import UserModule from "@/store/modules/user";
import {deepClone} from "@/helpers/helpers";
import FeedbackNotificationModule from "@/store/modules/feedback_notification";

export default {
    name: "EmailingDialogEmailAddressesTab",
    components: {
        IntegrationButton,
        EditEmailIntegration
    },
    props: {
        isSelected: {
            type: Boolean,
            default: false
        }
    },
    setup(props, ctx) {
        const {showFeedbackNotification} = FeedbackNotificationModule();
        const rightsModule = RightsModule()
        const {current_account} = AccountModule();
        const { loadMembers, getVisibleMembersByCompany } = UserModule()
        const dialogModule = DialogModule();

        const emailIntegrationsEnabled = ref(false)
        const { info_email } = AppModule()
        const {appStoreDialogGetters} = appStoreDialog()
        const emailAutomationEmails = ref([])
        const selectedEmailId = ref(null);
        const defaultEmailIntegrationId = ref(null);
        const showConnectSmtp = ref(false);
        const errors = ref(null);
        const companyId = computed(() => appStoreDialogGetters.getSelectedCompany().id)
        const accountFeatureSettings = appStoreDialogGetters.getCurrentFeatureSettings()
        const defaultSmtpSettings = ref(accountFeatureSettings && accountFeatureSettings.data ? accountFeatureSettings.data.default_smtp : null)

        emailIntegrationsEnabled.value = accountFeatureSettings.data.email_integrations_enabled ? accountFeatureSettings.data.email_integrations_enabled : false
        defaultEmailIntegrationId.value = get(accountFeatureSettings, `data.default_email_integration_id.${current_account.value.id}`, null)

        const selectedEmail = computed(() => {
            if (showConnectSmtp.value) {
                return {
                    integration_type: 'smtp',
                    visibility: 'private',
                    smtp: {
                        email_server: defaultSmtpSettings.value ? defaultSmtpSettings.value['email_server'] : 'other',
                        password: '',
                        host: defaultSmtpSettings.value ? defaultSmtpSettings.value['host'] : '',
                        port: defaultSmtpSettings.value ? defaultSmtpSettings.value['port'] : '',
                        security: defaultSmtpSettings.value ? defaultSmtpSettings.value['security'] : '',
                        default_smtp_server_for_company: false,
                    },
                    email: '',
                    sender_name: '',
                    reply_to_email: '',
                    default_address: false,
                    owner_id: current_account.value.id
                }
            } else {
                let email = emailAutomationEmails.value.find(x => x.id === selectedEmailId.value)
                if (!email) {
                    return null
                }

                email = deepClone(email)

                if (email.id === defaultEmailIntegrationId.value) {
                    email.default_address = true
                } else {
                    email.default_address = false
                }

                if (email.integration_type === 'smtp' && defaultSmtpSettings.value && defaultSmtpSettings.value.email_integration_id === email.id) {
                    email.smtp.default_smtp_server_for_company = true
                }

                return email
            }
        })

        const onWsMessage = async (data) => {
            if (data.email) {
                if (data.email.id) {
                    let index = emailAutomationEmails.value.findIndex(x => x.id === data.email.id)

                    if (index >= 0) {
                        emailAutomationEmails.value[index] = data.email
                        showFeedbackNotification($t('Email integration reconnected'))
                    } else {
                        emailAutomationEmails.value.push(data.email)
                    }
                }
                setEditEmailIntegration(null)
                await Vue.nextTick();
                setEditEmailIntegration(data.email.id)

                if (!emailIntegrationsEnabled.value) {
                    await setEmailIntegrationsEnabled(true)
                }
            }
        }

        const webSocket = reactive(new WS(onWsMessage, '/ws/email_integration'))

        onMounted(async () => {
            await loadMembers()

            webSocket.connect()

            emailAutomationEmails.value = (await ApiEmailIntegration.getEmailIntegrations(
                appStoreDialogGetters.getSelectedCompany().id
            )).email_automation_emails
        })

        onUnmounted(() => {
            webSocket.close()
        })

        const integrationSelected = (link) => {
            window.open(window.location.origin + link, '_blank').focus();
        }

        const onDeleteConfirmed = async () => {
            try {
                await ApiEmailIntegration.deleteEmailIntegration(selectedEmailId.value)
                emailAutomationEmails.value = emailAutomationEmails.value.filter(x => x.id !== selectedEmailId.value)
                selectedEmailId.value = null
            } catch (e) {
                errorDialog.showError(e)
            }
        }

        const onDelete = () => {
            dialogModule.setConfirmOptions({
                title: $t("Are you sure you want to delete this email integration?"),
                confirm_text: $t("Delete"),
                cancel_text: $t("Cancel"),
                action: () => onDeleteConfirmed(),
            });
            dialogModule.openConfirmDialog();
        }

        const saveEmailIntegration = async (email) => {
            const data = await ApiEmailIntegration.saveEmailIntegration(
                selectedEmailId.value,
                email.integration_type,
                email.visibility,
                email.email,
                email.reply_to_email,
                email.sender_name,
                email.owner_id,
                appStoreDialogGetters.getSelectedCompany().id,
                email.default_address,
                email.smtp
            )

            if (email.default_address) {
                defaultEmailIntegrationId.value = data.email.id
            } else if (!email.default_address && defaultEmailIntegrationId.value === data.email.id) {
                defaultEmailIntegrationId.value = null
            }

            const existingEmailIndex = emailAutomationEmails.value.findIndex(x => x.id === data.email.id)

            if (existingEmailIndex > -1) {
                emailAutomationEmails.value.splice(existingEmailIndex, 1, data.email)
            } else {
                emailAutomationEmails.value.push(data.email)
            }

            defaultSmtpSettings.value = data.default_smtp
        }

        const saveDefaultEmail = async (email) => {
            await ApiEmailIntegration.saveDefault(companyId.value, email.id, email.default_address)
            if (email.default_address) {
                defaultEmailIntegrationId.value = email.id
            } else if (!email.default_address && defaultEmailIntegrationId.value === email.id) {
                defaultEmailIntegrationId.value = null
            }
        }

        const onSave = async (email) => {
            try {
                errors.value = null

                if ((email.owner_id === current_account.value.id || !email.owner_id) || rightsModule.can_edit_account(companyId.value)) {
                    await saveEmailIntegration(email)
                } else {
                    await saveDefaultEmail(email)
                }

                selectedEmailId.value = null
                showConnectSmtp.value = false

                if (!emailIntegrationsEnabled.value) {
                    await setEmailIntegrationsEnabled(true)
                }
            } catch (e) {
                if (e.response.data.error_type === 'form_validation') {
                    errors.value = e.response.data.errors
                } else {
                    errorDialog.showError(e)
                }
            }
        }

        const connectSmtp = async () => {
            showConnectSmtp.value = true
            errors.value = null
        }

        const onClose = () => {
            showConnectSmtp.value = false
            selectedEmailId.value = null
        }

        const canEditEmailIntegrationsEnabled = computed(() => rightsModule.can_edit_account(appStoreDialogGetters.getSelectedCompany().id))

        const setEmailIntegrationsEnabled = async (value) => {
            try {
                let data = appStoreDialogGetters.getCurrentFeatureSettings() || {}
                data['email_integrations_enabled'] = value

                const accountFeature = appStoreDialogGetters.getCurrentFeature()
                const selectedAccount = appStoreDialogGetters.getSelectedCompany()

                await ApiAppStore.saveAppSettings(
                    selectedAccount.id,
                    accountFeature.feature_id,
                    data
                )

                emailIntegrationsEnabled.value = value

            } catch (e) {
                errorDialog.showError(e)
            }
        }

        const sendTestEmail = async (email) => {
            try {
                await ApiEmailIntegration.sendTestEmail(
                    email.id,
                    email.integration_type,
                    companyId.value,
                    email.smtp ? email.smtp.host : null,
                    email.smtp ? email.smtp.port : null,
                    email.email,
                    email.smtp ? email.smtp.password : null,
                    email.smtp ? email.smtp.security : null,
                    email.sender_name
                )

                showFeedbackNotification($t('Test email sent'))
            } catch (e) {
                if (e.response.data.error_type === 'form_validation') {
                    if (e.response.data.errors[0].field === 'global') {
                        errorDialog.showError(e)
                    }

                    errors.value = e.response.data.errors
                } else {
                    errorDialog.showError(e)
                }
            }
        }

        const onReconnect = (email) => {
            if (email.integration_type === 'gmail') {
                window.open(window.location.origin + `/api/email_automation/gmail/get_token/${companyId.value}/${email.id}`, '_blank').focus();
            } else if (email.integration_type === 'office365') {
                window.open(window.location.origin + `/api/email_automation/outlook/get_token/${companyId.value}/${email.id}`, '_blank').focus();
            }
        }

        const setEditEmailIntegration = (emailIntegrationId) => {
            selectedEmailId.value = emailIntegrationId
            errors.value = null
        }

        const clearErrors = (field) => {
            if (errors.value) {
                errors.value = errors.value.filter(x => x.field !== field)
            }
        }

        return {
            emailIntegrationsEnabled,
            integrationSelected,
            emailAutomationEmails,
            selectedEmail,
            selectedEmailId,
            onDelete,
            onSave,
            connectSmtp,
            onClose,
            canEditEmailIntegrationsEnabled,
            setEmailIntegrationsEnabled,
            defaultEmailIntegrationId,
            EmailIntegrationModule,
            companyId,
            errors,
            sendTestEmail,
            clearErrors,
            getVisibleMembersByCompany,
            setEditEmailIntegration,
            defaultSmtpSettings,
            onReconnect,
            info_email
        };
    }
};
</script>
